/**
 * Session state + reducer, including bounded undo/redo history (spec §4.1 #3
 * requires at least 50 steps). Pure and framework-free so the history logic is
 * unit-tested without React. Every {@link LoadedDocument} mutation goes through
 * `setDoc`, so undo/redo covers inline edits, find/replace, merge/split, and QC
 * fixes alike.
 */
import type { LoadedDocument } from './session';
import type { SubtitleDocument } from '../../core';

/** Maximum retained undo steps — comfortably above the spec's 50-step minimum. */
export const HISTORY_LIMIT = 100;

export interface SessionState {
  loaded: LoadedDocument | null;
  error: string | null;
  /** Name of a Pro format a free user tried to import, else null (H1). */
  proImportLocked: string | null;
  past: SubtitleDocument[];
  future: SubtitleDocument[];
  /**
   * Whether the document has been mutated since it was loaded or last
   * exported (`markClean`). Guards destructive loads and page unloads.
   */
  dirty: boolean;
}

export type SessionAction =
  | { type: 'load'; loaded: LoadedDocument }
  | { type: 'error'; message: string }
  | { type: 'proLocked'; format: string }
  | { type: 'setDoc'; doc: SubtitleDocument }
  | { type: 'setQcAccepted'; keys: string[] }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'markClean' }
  | { type: 'clear' };

export const initialSessionState: SessionState = {
  loaded: null,
  error: null,
  proImportLocked: null,
  past: [],
  future: [],
  dirty: false,
};

function withDoc(loaded: LoadedDocument, doc: SubtitleDocument): LoadedDocument {
  return { ...loaded, doc };
}

export function sessionReducer(state: SessionState, action: SessionAction): SessionState {
  switch (action.type) {
    case 'load':
      return {
        loaded: action.loaded,
        error: null,
        proImportLocked: null,
        past: [],
        future: [],
        dirty: false,
      };

    case 'error':
      return { ...state, error: action.message, proImportLocked: null };

    case 'proLocked':
      return { ...state, proImportLocked: action.format, error: null };

    case 'setDoc': {
      if (!state.loaded) return state;
      const past = [...state.past, state.loaded.doc].slice(-HISTORY_LIMIT);
      return { ...state, loaded: withDoc(state.loaded, action.doc), past, future: [], dirty: true };
    }

    case 'setQcAccepted': {
      // QC accept/dismiss (cue-forge-n3e.7) is review metadata, NOT a document
      // edit: keep the `doc` reference (so the QC auto-rerun, keyed on doc
      // identity, doesn't fire and autosave doesn't re-serialize the model),
      // push no history, and leave `dirty` alone (no spurious unload warning).
      if (!state.loaded) return state;
      return { ...state, loaded: { ...state.loaded, qcAccepted: action.keys } };
    }

    case 'undo': {
      if (!state.loaded || state.past.length === 0) return state;
      const prev = state.past[state.past.length - 1]!;
      return {
        ...state,
        loaded: withDoc(state.loaded, prev),
        past: state.past.slice(0, -1),
        future: [state.loaded.doc, ...state.future].slice(0, HISTORY_LIMIT),
        dirty: true,
      };
    }

    case 'redo': {
      if (!state.loaded || state.future.length === 0) return state;
      const next = state.future[0]!;
      return {
        ...state,
        loaded: withDoc(state.loaded, next),
        past: [...state.past, state.loaded.doc].slice(-HISTORY_LIMIT),
        future: state.future.slice(1),
        dirty: true,
      };
    }

    case 'markClean':
      return state.dirty ? { ...state, dirty: false } : state;

    case 'clear':
      return initialSessionState;
  }
}
